import React, { useState, useRef, useEffect, useContext } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import { RiImageAddLine } from 'react-icons/ri';
import { MdWorkspacePremium } from 'react-icons/md';
import { IoClose } from 'react-icons/io5';
import { Language } from '../../../App';
import '../../feeds/postTabs/formTheme.css';

const CertificateModal = ({ isOpen, toggle, ImagePreview, onSave, initialCertificate }) => {
    const [file, setFile] = useState(null);
    const [fileSrc, setFileSrc] = useState(null);
    const [certificateTitle, setCertificateTitle] = useState("");
    const fileInputRef = useRef();
    const { lang } = useContext(Language);

    useEffect(() => {
        if (initialCertificate) {
            setFile(initialCertificate.file);
            setFileSrc(initialCertificate.fileSrc);
            setCertificateTitle(initialCertificate.title || "");
        } else {
            setFile(null);
            setFileSrc(null);
            setCertificateTitle("");
        }
    }, [initialCertificate]);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            const reader = new FileReader();
            reader.onloadend = () => {
                setFileSrc(reader.result);
            };
            reader.readAsDataURL(selectedFile);
        }
    };

    const handleRemoveFile = () => {
        setFile(null);
        setFileSrc(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSave = () => {
        if (!file || !certificateTitle.trim()) return;

        const newCertificate = {
            title: certificateTitle.trim(),
            file,
            fileSrc,
        };

        if (initialCertificate && initialCertificate.index !== undefined) {
            onSave(initialCertificate.index, newCertificate);
        } else {
            onSave(null, newCertificate);
        }

        toggle();
    };

    return (
        <Modal 
            isOpen={isOpen} 
            toggle={toggle} 
            centered 
            scrollable
            zIndex={1070}
            container="body"
            dialogClassName="certificate-modal-dialog"
            contentClassName="certificate-modal-content border-0 shadow-lg rounded-4 overflow-hidden"
            style={{ maxWidth: "480px" }}
        >
            {/* Header */}
            <div 
                className="d-flex align-items-center justify-content-between px-3 px-sm-4 py-3 border-bottom"
                style={{ borderColor: "#EEF2F6", background: "#FFFFFF" }}
            >
                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                    <div 
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: "34px", height: "34px", background: "#E0F7FC", color: "#00BADA" }}
                    >
                        <MdWorkspacePremium size={20} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <h5 className="mb-0 fw-bold text-truncate" style={{ color: "#0B132B", fontSize: "16px" }}>
                            {initialCertificate ? "Edit Certificate" : (lang.certificate_by || "Accredited Certificate")}
                        </h5>
                        <span className="text-muted text-truncate d-block" style={{ fontSize: "11.5px" }}>
                            Attach completion certificate issued to doctors
                        </span>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={toggle}
                    className="btn btn-sm btn-light rounded-circle p-1 d-flex align-items-center justify-content-center flex-shrink-0 ms-2"
                    style={{ width: "28px", height: "28px", color: "#64748B" }}
                    aria-label="Close"
                >
                    <IoClose size={18} />
                </button>
            </div>

            {/* Modal Body */}
            <ModalBody className="px-3 px-sm-4 py-3">
                <div className="pf-scope-publish pf-form-body d-flex flex-column gap-3">
                    {/* Document / Image Upload */}
                    <div className="pf-field">
                        <div className="pf-label">
                            <span className="pf-label-text">
                                <MdWorkspacePremium className="pf-label-icon" />
                                {lang.add_certificate_image || "Certificate Image / Document"}
                            </span>
                            <span className="text-danger fs-12">*</span>
                        </div>

                        {fileSrc ? (
                            <div 
                                className="position-relative rounded-3 overflow-hidden border"
                                style={{ height: "160px", background: "#0F172A", cursor: "pointer" }}
                                onClick={() => ImagePreview && ImagePreview(fileSrc)}
                            >
                                <img
                                    src={fileSrc}
                                    alt="Certificate Preview"
                                    className="w-100 h-100"
                                    style={{ objectFit: "cover" }}
                                />
                                <div
                                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                    style={{ background: "rgba(0,0,0,0.3)", opacity: 0, transition: "opacity 0.2s" }}
                                    onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                                    onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
                                >
                                    <span className="badge bg-dark bg-opacity-75 text-white px-3 py-1.5 rounded-pill fs-12">
                                        Click to preview
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light position-absolute top-0 end-0 m-2 rounded-circle p-1 d-flex align-items-center justify-content-center shadow-sm"
                                    style={{ width: "28px", height: "28px", zIndex: 5 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveFile();
                                    }}
                                    title="Remove file"
                                >
                                    <IoClose size={16} />
                                </button>
                            </div>
                        ) : (
                            <div
                                className="pf-upload-zone"
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            >
                                <div className="pf-upload-zone-left">
                                    <div className="pf-upload-icon-circle">
                                        <RiImageAddLine size={20} />
                                    </div>
                                    <div className="pf-upload-zone-text">
                                        <div className="pf-upload-zone-title">
                                            {lang.add_certificate_image || "Upload Certificate Image"}
                                        </div>
                                        <div className="pf-upload-zone-sub">
                                            PNG, JPG or WebP (Official template or badge)
                                        </div>
                                    </div>
                                </div>
                                <div className="pf-upload-actions">
                                    <span
                                        className="btn btn-sm rounded-pill fw-semibold"
                                        style={{
                                            background: "#FFFFFF",
                                            border: "1px solid var(--pf-accent-border)",
                                            color: "var(--pf-accent-dark)",
                                            fontSize: "12px",
                                            padding: "5px 12px",
                                        }}
                                    >
                                        Browse
                                    </span>
                                </div>
                            </div>
                        )}

                        <input
                            type="file"
                            ref={fileInputRef}
                            className="d-none"
                            accept="image/*"
                            onChange={handleFileChange}
                        />
                    </div>

                    {/* Certificate Title */}
                    <div className="pf-field">
                        <div className="pf-label">
                            <span className="pf-label-text">
                                Certificate Title
                            </span>
                            <span className="text-danger fs-12">*</span>
                        </div>
                        <input
                            type="text"
                            className="pf-input"
                            placeholder="e.g. Masterclass Board of Surgery Accreditation"
                            value={certificateTitle}
                            onChange={(e) => setCertificateTitle(e.target.value)}
                        />
                    </div>

                    {/* Footer Actions */}
                    <div className="pf-footer-row mt-2" style={{ justifyContent: "flex-end" }}>
                        <div className="pf-footer-actions">
                            <button
                                type="button"
                                className="pf-btn-cancel"
                                onClick={toggle}
                            >
                                {lang.cancel || "Cancel"}
                            </button>
                            <button
                                type="button"
                                className="pf-btn-submit"
                                disabled={!file || !certificateTitle.trim()}
                                onClick={handleSave}
                            >
                                <span>{lang.save || "Save Certificate"}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default CertificateModal;
